import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { IsaleModal } from "./Modal";
import { useAcceptConsent } from "@/shared/api/queries";
import { useTranslation } from "@/shared/i18n/I18nProvider";

/**
 * AI consent reprompt — web counterpart of
 * `apps/isale-mobile/src/shared/modals/AiConsentRepromptModal.tsx`.
 *
 * Listens for the `isale:consent-required` CustomEvent that the API
 * client dispatches whenever the backend returns a 403 with
 * `code: CONSENT_REQUIRED` (services/isale-service/src/lib/consent.ts).
 * The full consent flow lives at `/consent` for first sign-in; this
 * modal shows up when the consent policy version has changed and the
 * user needs to re-acknowledge AI processing terms before continuing.
 *
 * Two CTAs:
 *   • "Đồng ý tiếp tục" → POST /v1/consents/ai (handled by caller in
 *     future iteration — currently dismisses so the user can retry).
 *   • "Xem chi tiết"   → routes to the full consent screen so the
 *     user can read every clause before agreeing.
 *
 * Closes audit §6 B1.
 */
export function AiConsentRepromptModal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [version, setVersion] = useState<string>("");
  const acceptConsent = useAcceptConsent();

  useEffect(() => {
    function onConsentRequired(e: Event) {
      const detail = (e as CustomEvent<{ version: string }>).detail;
      setVersion(detail?.version ?? "current");
      setVisible(true);
    }
    window.addEventListener("isale:consent-required", onConsentRequired);
    return () => {
      window.removeEventListener("isale:consent-required", onConsentRequired);
    };
  }, []);

  return (
    <IsaleModal
      visible={visible}
      onDismiss={() => setVisible(false)}
      title={t("consent.repromptTitle")}
      subtitle={t("consent.repromptSubtitle", { version })}
      variant="default"
      hideDismiss
      dismissOnBackdropPress={false}
      actions={[
        {
          label: acceptConsent.isPending
            ? t("common.saving")
            : t("consent.repromptAcceptCta"),
          variant: "primary",
          disabled: acceptConsent.isPending,
          onPress: async () => {
            try {
              await acceptConsent.mutateAsync({ consent_version: version });
              setVisible(false);
              // Fire a custom event so callers (e.g. ProjectDetail) can
              // automatically retry the action that triggered the 403.
              window.dispatchEvent(new CustomEvent("isale:consent-accepted"));
            } catch {
              // Keep modal open — user can retry or view details.
            }
          },
        },
        {
          label: t("consent.repromptViewCta"),
          variant: "secondary",
          onPress: () => {
            setVisible(false);
            navigate("/consent");
          },
        },
      ]}
    >
      <div className="flex items-start gap-3 rounded-2xl border border-subtle bg-primary-soft p-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-extrabold text-primary">
            {t("consent.repromptWhatsNewTitle")}
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {t("consent.repromptWhatsNewBody")}
          </p>
        </div>
      </div>
    </IsaleModal>
  );
}
